import React, { useCallback, useState } from "react";
import { commitMutation, graphql, QueryRenderer, useFragment } from "react-relay";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";
import { Loader } from "semantic-ui-react";

import type { NodesFromEdges } from "~/lib/utils";
import { environment } from "~/models";
import { recurrenceRuleToString } from "~/stream/utils";
import { Button, useModals } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import { useEventState } from "./useEventState";

import type {
	LinkEventModal_stream$data,
	LinkEventModal_stream$key,
} from "~/__generated__/LinkEventModal_stream.graphql";
import type { LinkEventModalMutation as MutationType } from "~/__generated__/LinkEventModalMutation.graphql";
import type { LinkEventModalQuery as QueryType } from "~/__generated__/LinkEventModalQuery.graphql";
import type { LinkEventModalRemoveEventMutation as RemoveEventMutationType } from "~/__generated__/LinkEventModalRemoveEventMutation.graphql";

import styles from "./css/LinkEventModal.module.css";

const LinkEventModalQuery = graphql`
	query LinkEventModalQuery($otherUser: ID!) {
		viewer {
			recurringOneononeEvents(otherUser: $otherUser) {
				edges {
					node {
						guid
						title
						recurrenceRule
						nextOccurrence {
							start
							end
						}
					}
				}
			}
		}
	}
`;

const UpdateLinkedEventMutation = graphql`
	mutation LinkEventModalMutation($input: UpdateLinkedEventInput!) {
		updateLinkedEvent(input: $input) {
			stream {
				id
				linkedEventGuid
				linkedEvent {
					id
					guid
					title
					recurrenceRule
					nextOccurrence {
						start
						end
					}
					finalOccurrence
				}
				claimedEvents {
					attendees {
						email
					}
				}
			}
		}
	}
`;

const RemoveLinkedEventMutation = graphql`
	mutation LinkEventModalRemoveEventMutation($input: RemoveLinkedEventInput!) {
		removeLinkedEvent(input: $input) {
			stream {
				id
				linkedEventGuid
				linkedEvent {
					id
					guid
					title
					recurrenceRule
					nextOccurrence {
						start
						end
					}
					finalOccurrence
				}
			}
		}
	}
`;

export const linkEventModalStreamFragment = graphql`
	fragment LinkEventModal_stream on NoteStream {
		...useEventState_stream

		id
		otherUser {
			id
			fullName
		}
		linkedEvent {
			id
			guid
			title
			recurrenceRule
			nextOccurrence {
				start
				end
			}
			finalOccurrence
		}
	}
`;

type EventProps = {
	isSelected?: boolean;
	className?: string;
	event:
		| NodesFromEdges<QueryType["response"]["viewer"]["recurringOneononeEvents"]>
		| NonNullable<LinkEventModal_stream$data["linkedEvent"]>;
	removable?: boolean;
	onClick?(guid: string): void;
	onRemove?(): void;
};

type ModalState = "initial" | "loading_events" | "selecting_event" | "no_events_available" | "confirm_remove_event";

function Event(props: EventProps) {
	const { isSelected, event, onClick, onRemove, className, removable } = props;
	if (!event.recurrenceRule?.[0]) return null;
	const isExpired = !event.nextOccurrence?.start;
	const nextStart = moment(event.nextOccurrence?.start || "").format("ddd, MMM D, hh:mma");
	const nextEnd = moment(event.nextOccurrence?.end || "").format("ddd, MMM D, hh:mma");
	const onClickEvent = () => {
		onClick && onClick(event.guid);
	};
	const onRemoveEvent = () => {
		onRemove && onRemove();
	};

	return (
		<div onClick={onClickEvent} className={cx(styles.eventContainer, className, { [styles.selected]: isSelected })}>
			{removable && (
				<div className={styles.eventRemoveBtn} onClick={onRemoveEvent}>
					<div className={styles.eventRemoveBtnOverlay}>
						<FontAwesomeIcon icon={faTimes} />
					</div>
				</div>
			)}
			<div className={styles.event}>
				<div>{event.title}</div>
				<div className={styles.eventDetails}>
					{!isExpired && (
						<div>
							<small>
								{nextStart} - {nextEnd}
							</small>
						</div>
					)}
					<div>
						<small>Repeats {recurrenceRuleToString(event.recurrenceRule)}</small>
					</div>
				</div>
			</div>
			<div className={styles.iconContainer}>
				<FontAwesomeIcon icon={faUsers} /> <small>2</small>
			</div>
		</div>
	);
}

export interface LinkEventModalProps {
	onClose: () => void;
	stream: LinkEventModal_stream$key;
}

export function useLinkEventModal(stream: LinkEventModal_stream$key) {
	const modals = useModals();
	const openModal = useCallback(
		() => modals.open(({ close }) => <LinkEventModal onClose={close} stream={stream} />),
		[modals, stream],
	);

	return openModal;
}

export default function LinkEventModal({ onClose, stream: streamKey }: LinkEventModalProps) {
	const stream = useFragment(linkEventModalStreamFragment, streamKey);
	const { id: streamId, otherUser, linkedEvent } = stream;

	const eventState = useEventState(stream);
	const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
	const [modalState, setModalState] = useState<ModalState>(linkedEvent != null ? "initial" : "loading_events");

	const onCloseModal = React.useCallback(() => {
		setModalState(linkedEvent ? "initial" : "loading_events");
		setSelectedEvent(undefined);
		onClose();
	}, [onClose, linkedEvent]);

	const onConfirm = React.useCallback(() => {
		if (selectedEvent == undefined) {
			toast.error("Please select an event.");
			return;
		}
		commitMutation<MutationType>(environment, {
			mutation: UpdateLinkedEventMutation,
			variables: { input: { streamId: streamId, guid: selectedEvent } },
			onCompleted: ({ updateLinkedEvent }, err) => {
				if (err || updateLinkedEvent?.stream?.linkedEvent == null) {
					toast.error("Sorry, something went wrong.");
				} else {
					toast.success(`"${updateLinkedEvent?.stream?.linkedEvent?.title}" is now linked to this 1-on-1.`);
				}

				setModalState("initial");
				setSelectedEvent(undefined);
			},
		});
		onClose();
	}, [onClose, streamId, selectedEvent]);

	const removeLinkedEvent = React.useCallback(() => {
		commitMutation<RemoveEventMutationType>(environment, {
			mutation: RemoveLinkedEventMutation,
			variables: { input: { streamId: streamId } },
			onCompleted: ({ removeLinkedEvent }, err) => {
				if (err || removeLinkedEvent?.stream?.linkedEvent != null) {
					toast.error("Sorry, something went wrong");
				} else {
					toast.success("The event has been unlinked from this 1-on-1.");
					setModalState("loading_events");
					setSelectedEvent(undefined);
					onClose();
				}
			},
		});
	}, [onClose, streamId]);

	const cancelRemoveLinkedEvent = React.useCallback(() => {
		setModalState("initial");
	}, []);

	const onClickEvent = React.useCallback((guid: string) => {
		setSelectedEvent(guid);
	}, []);

	const clickShowEventSelector = React.useCallback(() => {
		setModalState("loading_events");
	}, []);

	const onClickRemoveEvent = React.useCallback(() => {
		setModalState("confirm_remove_event");
	}, []);

	const renderEventList = React.useCallback(
		({ props }: { props: QueryType["response"] | null }) => {
			const events = props?.viewer.recurringOneononeEvents?.edges.map(edge => edge && edge.node);
			if (events == null || !otherUser) return <></>;
			const availableEvents = events.filter(e => e?.guid != linkedEvent?.guid && e?.nextOccurrence);
			setModalState(availableEvents.length > 0 ? "selecting_event" : "no_events_available");

			if (modalState == "no_events_available") {
				return (
					<>
						<p className={styles.redText}>
							We were not able to find any events between you and {otherUser.fullName}.<br />
							Please create a new meeting in the meetings panel.
						</p>
						{eventState == "invalid" && (
							<Button onClick={onClickRemoveEvent} variant="primary" size="slim">
								Clear invalid event
							</Button>
						)}
					</>
				);
			}
			const eventsList = availableEvents.map(event => {
				if (event == null) return <></>;
				return <Event onClick={onClickEvent} event={event} isSelected={selectedEvent == event.guid} key={event.guid} />;
			});
			return (
				<>
					<div className={styles.eventListContainer}>{eventsList}</div>
					{eventState == "invalid" && (
						<Button onClick={onClickRemoveEvent} variant="primary" size="slim">
							Clear invalid event
						</Button>
					)}
				</>
			);
		},
		[linkedEvent, modalState, selectedEvent, onClickEvent, otherUser, eventState, onClickRemoveEvent],
	);

	const modalHeader = React.useMemo(() => {
		if (modalState == "confirm_remove_event") {
			return "Are you sure?";
		}
		switch (eventState) {
			case "no_event":
				return "Select a calendar event";
			case "invalid":
				return "Select a calendar event";
			case "has_upcoming":
				return "Stream calendar event";
			case "expired":
				return (
					<>
						Calendar event <span className={styles.redText}>expired</span>
					</>
				);
			case "expiring":
				return (
					<>
						Calendar event <span className={styles.yellowText}>expiring</span>
					</>
				);
		}
	}, [eventState, modalState]);

	const modalBody = React.useMemo(() => {
		if (modalState == "confirm_remove_event") {
			return (
				<p>
					By removing this event, you and your teammate will no longer receive notifications to prepare for your
					upcoming 1-on-1s.
				</p>
			);
		}
		if (modalState == "initial" && eventState != "invalid" && linkedEvent != null) {
			const eventStyles = {
				[styles.yellowBorder]: eventState == "expiring",
				[styles.redBorder]: eventState == "expired",
			};

			return (
				<div className={styles.linkedEventContainer}>
					<p className={styles.body}>Your note series is currently associated with this event.</p>
					<Event
						event={linkedEvent}
						removable
						onRemove={onClickRemoveEvent}
						className={cx(styles.readOnly, eventStyles)}
					/>
					<small>
						<strong>Note:</strong> To modify this event, visit your work calendar.
					</small>
				</div>
			);
		}

		if (!otherUser) return <></>;

		return (
			<div className={styles.eventListContainer}>
				<p className={cx(styles.body, styles.bodyLeftAligned)}>
					Stay in sync with <strong>{otherUser.fullName}</strong> by selecting a recurring 1-on-1 calendar event for
					this note series.
				</p>
				{modalState == "loading_events" && (
					<div className={styles.loadingEmpty}>
						<Loader inline />
					</div>
				)}
				<QueryRenderer<QueryType>
					variables={{ otherUser: otherUser.id }}
					environment={environment}
					query={LinkEventModalQuery}
					render={renderEventList}
				/>
			</div>
		);
	}, [modalState, linkedEvent, eventState, otherUser, renderEventList, onClickRemoveEvent]);

	const modalButtons = React.useMemo(() => {
		switch (modalState) {
			case "selecting_event":
				return (
					<Button size="normal" variant="primary" disabled={selectedEvent == null} onClick={onConfirm}>
						Confirm
					</Button>
				);
			case "no_events_available":
				return (
					<Button size="normal" onClick={onCloseModal}>
						Ok
					</Button>
				);
			case "initial":
				return (
					<>
						<Button size="normal" onClick={onCloseModal}>
							Close
						</Button>
						<Button size="normal" onClick={clickShowEventSelector}>
							Change event
						</Button>
					</>
				);
			case "confirm_remove_event":
				return (
					<>
						<Button size="normal" onClick={cancelRemoveLinkedEvent}>
							Cancel
						</Button>
						<Button size="normal" onClick={removeLinkedEvent} variant="destructive">
							Remove
						</Button>
					</>
				);
			default:
				return (
					<Button size="normal" onClick={onCloseModal}>
						Close
					</Button>
				);
		}
	}, [
		modalState,
		selectedEvent,
		onConfirm,
		onCloseModal,
		clickShowEventSelector,
		removeLinkedEvent,
		cancelRemoveLinkedEvent,
	]);

	return (
		<Modal onClose={onCloseModal} width="28.75rem">
			<Modal.Header onClose={onCloseModal}>
				<Modal.Title>{modalHeader}</Modal.Title>
			</Modal.Header>
			<Modal.Body>{modalBody}</Modal.Body>
			<Modal.Footer>{modalButtons}</Modal.Footer>
		</Modal>
	);
}
